/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import classNames from 'classnames'
import { Text } from '@hashicorp/mds-react/components'
import sendPosthogEvent from '@hashicorp/react-components/src/lib/send-posthog-event'
import { useActiveSection } from './use-active-section'
import s from './table-of-contents.module.css'

export interface ITableOfContents {
	headings: IHeading[]
}

export interface IHeading {
	text: string
	id: string
}

export const TableOfContents = ({ headings }: ITableOfContents) => {
	const slugs = headings.map((heading) => {
		return heading.id
	})
	const activeSection = useActiveSection(slugs)

	// This event logging will be used to temporarily determine ToC engagement.
	// If low, it may not make sense to support this functionality.
	const handleHeadingClick = (heading: string) => {
		sendPosthogEvent('TOC Click', { heading })
	}

	return (
		<ul className={s.root}>
			{headings.map((item, index) => {
				const isActive = activeSection === item.id
				return (
					<li key={`${item.id}-${index}`}>
						<a
							className={s.itemLink}
							href={`#${item.id}`}
							onClick={() => {
								handleHeadingClick(item.text)
							}}
						>
							<div className={classNames(s.bar, { [s.active]: isActive })} />
							<Text.DisplayExpressive
								className={s.heading}
								size="100"
								color={isActive ? 'strong' : 'faint'}
								weight="medium"
							>
								{item.text}
							</Text.DisplayExpressive>
						</a>
					</li>
				)
			})}
		</ul>
	)
}
